import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { interval } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { Endpoint, HelpIndex } from '../../../../models/enums';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { ObjectActionListType, ActionType, Permissions } from '../../../../models/permission';

@Component({
	standalone: false,
	selector: 'res-virus-detection',
	templateUrl: './virus-detection.component.html',
	styleUrls: ['./virus-detection.component.scss']
})
export class VirusDetectionComponent implements OnInit, OnDestroy {
	@HostBinding('class') classes = 'maximized-component';
	readonly defaultLabel = 'N/A';
	automationId = 'virus-detection';
	versionNumber: string;
	isUpdating: boolean;
	lastUpdatedDateTime: string;
	updateStatus: string;
	isStorageDepotConfigured: boolean;
	isBusy: boolean;
	loadDefinitionsPoller = null;
	hasCreatePermissions: boolean;
	isAutoUpdateEnabled: boolean;

	constructor(private restService: RestService,
		private commonService: CommonService) { }

	ngOnInit(): void {
		this.hasCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.VIRUS_DETECTION, ActionType.CREATE);
		this.getSystemStorageDepot();
	}

	getSystemStorageDepot(): void {
		this.isBusy = true;
		this.restService.post(Endpoint.realmManager_getSystemStorageDepot, { systemScope: true }).then((storageDepotResponse) => {
			if (storageDepotResponse?.systemStorageDepotDto?.depotId) {
				this.isStorageDepotConfigured = true;
				this.loadVirusDefinitions(true);
			} else {
				this.isBusy = false;
				this.updateStatus = 'A System Storage Depot must be defined before updates can occur.';
				this.versionNumber = this.defaultLabel;
				this.lastUpdatedDateTime = this.defaultLabel;
			}
		}, () => { this.isBusy = false; });
	}

	loadVirusDefinitions(onInitLoad = false): void {
		this.restService.post(Endpoint.realmManager_getVirusDefinitions, { systemScope: true }).then((response) => {
			if (response) {
				this.isAutoUpdateEnabled = response.enabled;
				this.versionNumber = response.version ? response.version : this.defaultLabel;
				this.updateStatus = response.updateStatus ? response.updateStatus : this.defaultLabel;
				this.isUpdating = response.running;
				this.isBusy = false;

				if (this.isUpdating) {
					if (onInitLoad) {
						this.setLastSuccesfulUpdateDateTime(response.updatedOn);
					}
					// will start poller only if it's not already started
					this.startLoadDefinitionsPoller();
				} else {
					this.setLastSuccesfulUpdateDateTime(response.updatedOn);
					this.stopLoadDefinitionsPoller();
				}
			}
		}, () => {
			this.isBusy = false;
			this.stopLoadDefinitionsPoller();
		});
	}

	setLastSuccesfulUpdateDateTime(timestamp: string): void {
		this.lastUpdatedDateTime = timestamp ? timestamp : this.defaultLabel;
	}

	onIsAutoUpdateChanged(): void {
		this.enableDisableAutoUpdate();
	}

	enableDisableAutoUpdate(): void {
		this.restService.post(Endpoint.realmManager_updateVirusDefinitions, {
			enabled: this.isAutoUpdateEnabled,
			frequency: 'DAILY',
			updateDefinitionFiles: false,
			systemScope: true
		});
	}
	updateVirusDefinitions(): void {
		this.isUpdating = true;
		//TODO-AFTER-BACKEND-FIX: 1. remove url !== Endpoint.realmManager_updateVirusDefinitions from http.service.ts 2. remove startLoadDefinitionsPoller/stopLoadDefinitionsPoller
		// currently incoming workbasketHandle is invalid and I blocked auto polling from http.service.ts and handled it here locally
		this.restService.post(Endpoint.realmManager_updateVirusDefinitions, { enabled: this.isAutoUpdateEnabled, frequency: 'DAILY', updateDefinitionFiles: true, systemScope: true }).then(() => {
			this.startLoadDefinitionsPoller();
		});
	}

	startLoadDefinitionsPoller(): void {
		if (!this.loadDefinitionsPoller) {
			const pollInterval = 5000; // 5 sec
			this.loadDefinitionsPoller = interval(pollInterval).pipe(startWith(0)
			).subscribe(() => this.loadVirusDefinitions());
		}
	}

	stopLoadDefinitionsPoller(): void {
		if (this.loadDefinitionsPoller) {
			this.loadDefinitionsPoller.unsubscribe();
			this.loadDefinitionsPoller = null;
		}
	}

	openHelp = () => this.commonService.openHelp(HelpIndex.sys_set_virus_detect_updates_dlg);

	ngOnDestroy(): void {
		this.stopLoadDefinitionsPoller();
	}
}

